import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CircularProgress, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';
import ShippingProvider from '@/shared/types/shipping/shippingProvider.type';

import useShippingProfileDetailsPanel from './hooks/useShippingProfileDetailsPanel';

type ShippingProfileDetailsPanelProps = {
  shippingProfile: ShippingProfile;
  shippingProviders: ShippingProvider[];
  templateSchemas: ProfilesTemplateSchemas;
  isLoading: boolean;
  onUpdateProfile: (value: Partial<ShippingProfile>) => void;
};

const ShippingProfileDetailsPanel: FC<ShippingProfileDetailsPanelProps> = ({
  shippingProfile,
  shippingProviders,
  templateSchemas,
  isLoading,
  onUpdateProfile,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const {
    profileDetailsForm,
    packagingPropertiesForm,
    formErrors,
    setProfileDetailsForm,
    setPackagingPropertiesForm,
    onValidate,
    getShippingProviderKey,
  } = useShippingProfileDetailsPanel({
    shippingProfile,
    shippingProviders,
  });

  return (
    <WaveUIAccordion
      defaultExpanded
      title={t('shipping:shippingProfileDetails')}
    >
      <Stack spacing={2} width='100%'>
        <WaveUIForm_V2
          fields={[
            {
              field_type: FieldType.Text,
              field_props: {
                required: true,
                name: 'display_name',
                label: t('forms:display_name'),
                placeholder: t('forms:display_name'),
                value: profileDetailsForm.display_name,
                error: !!formErrors?.display_name,
                helperText: formErrors?.display_name,
              },
            },
            {
              field_type: FieldType.Select,
              field_props: {
                name: 'shipping_provider_id',
                label: t('forms:shipping_provider_id'),
                placeholder: t('forms:shipping_provider_id'),
                value: profileDetailsForm.shipping_provider_id,
                options: shippingProviders.map(
                  ({ shipping_provider_id, name }) => ({
                    value: shipping_provider_id,
                    label: name,
                  }),
                ),
                renderOption: (option) => {
                  const providerById = shippingProviders.find(
                    (item) => item.shipping_provider_id === option.value,
                  );

                  return (
                    <Stack spacing={0.5} direction='row' alignItems='center'>
                      <Typography variant='body1'>
                        {providerById?.name}:
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {providerById?.shipping_provider_key}
                      </Typography>
                    </Stack>
                  );
                },
              },
            },
            {
              field_type: FieldType.Select,
              field_props: {
                name: 'shipping_method_id',
                label: t('forms:shipping_method_id'),
                placeholder: t('forms:shipping_method_id'),
                value: profileDetailsForm.shipping_method_id,
                options: templateSchemas.methods.map((item) => ({
                  label: `${item.carrier_name} - ${item.service_name}`,
                  value: item.shipping_method_id,
                })),
              },
            },
            {
              field_type: FieldType.Text,
              field_props: {
                name: 'category',
                label: t('forms:category'),
                placeholder: t('forms:category'),
                value: profileDetailsForm.category,
              },
            },
          ]}
          getFieldsValues={(value) => {
            if ('shipping_provider_id' in value) {
              const id = value.shipping_provider_id as string;

              return setProfileDetailsForm((prev) => ({
                ...prev,
                shipping_provider_id: id,
                shipping_provider_key: getShippingProviderKey(id),
              }));
            }

            setProfileDetailsForm((prev) => ({ ...prev, ...value }));
          }}
        />

        {shippingProfile.packaging_properties && (
          <WaveUIForm_V2
            fields={Object.entries(packagingPropertiesForm).map(
              ([key, value]) => {
                return {
                  columns: 6,
                  field_type: FieldType.Switch,
                  field_props: {
                    name: key,
                    label: t(`forms:${key}`),
                    checked: value as boolean,
                  },
                };
              },
            )}
            getFieldsValues={(value) => {
              setPackagingPropertiesForm((prev) => ({ ...prev, ...value }));
            }}
          />
        )}
      </Stack>

      <Button
        variant='contained'
        sx={{ width: 'max-content' }}
        startIcon={
          isLoading && (
            <CircularProgress size='16px' color='inherit' sx={{ mr: 1 }} />
          )
        }
        onClick={() => {
          const isValid = onValidate();

          if (isValid && !isLoading) {
            void onUpdateProfile({
              ...profileDetailsForm,
              packaging_properties: {
                ...packagingPropertiesForm,
              },
            });
          }
        }}
      >
        {t('common:saveChanges')}
      </Button>
    </WaveUIAccordion>
  );
};

export default ShippingProfileDetailsPanel;
